import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, HeartHandshake, Sparkles, Compass, Coins } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import ShareButton from "@/components/ShareButton";
import VoiceIntro from "@/components/VoiceIntro";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1764867249027-06a1db1c6613?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzb2Z0JTIwd2FybSUyMHN1bmxpdCUyMGludGVyaW9yJTIwY2FsbXxlbnwwfHx8fDE3ODM5NzgwMTd8MA&ixlib=rb-4.1.0&q=85";
const HEALING = "https://images.unsplash.com/photo-1447619297994-b829cc1ab44a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxoZWFsaW5nJTIwaGFuZHMlMjBzb2Z0JTIwbmF0dXJhbCUyMGxpZ2h0fGVufDB8fHx8MTc4Mzk3ODAxN3ww&ixlib=rb-4.1.0&q=85";
const WHEAT = "https://images.unsplash.com/photo-1666631740049-5643ffd94b7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHx3aGVhdCUyMGZpZWxkJTIwZ29sZGVuJTIwaG91ciUyMGNhbG18ZW58MHx8fHwxNzgzOTc4MDE3fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useLang();
  const [services, setServices] = useState([]);
  const [fundTotal, setFundTotal] = useState(null);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/fundraiser").then((r) => {
      const eur = (r.data.totals || {}).EUR || 0;
      setFundTotal(eur);
    }).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="container-refined pt-14 md:pt-24 pb-14 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1 animate-fade-up">
          <p className="eyebrow" data-testid="home-eyebrow">{t("home.eyebrow")}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-forest-900">
            <span className="block">{t("home.title_line1")}</span>
            <span className="block italic text-wheat-700">{t("home.title_line2")}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-forest-700 max-w-xl">{t("home.lede")}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/book" className="btn-primary" data-testid="home-cta-book">
              {t("home.cta_book")} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
            <Link to="/what-is-reiki" className="btn-secondary" data-testid="home-cta-reiki">
              {t("home.cta_reiki")}
            </Link>
            <ShareButton variant="ghost" />
          </div>
          <div className="mt-6">
            <VoiceIntro />
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3 text-xs text-forest-700/80">
            <Link to="/fundraiser" className="inline-flex items-center gap-2 rounded-full bg-wheat-50 border border-wheat-500/30 px-3 py-1.5 hover:bg-wheat-100 transition" data-testid="home-fund-badge">
              <Coins className="h-3.5 w-3.5 text-wheat-700" strokeWidth={1.5} />
              <span>{t("pricing.send_note")}</span>
              {fundTotal != null && fundTotal > 0 && (
                <span className="ml-2 text-forest-700">· €{Number(fundTotal).toFixed(0)} sent</span>
              )}
            </Link>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <div className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(44,62,53,0.15)]">
            <img src={HERO} alt="Warm sunlit interior" className="w-full h-[420px] md:h-[560px] object-cover" />
            <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-ivory/85 backdrop-blur-md p-5 border border-white/60">
              <p className="text-[11px] uppercase tracking-[0.25em] text-wheat-700">— Sunbraider</p>
              <p className="mt-1 text-sm text-forest-800 leading-snug">A first chapter: presence, service, and quiet devotion.</p>
            </div>
          </div>
          <div className="absolute -top-6 -left-6 hidden md:flex h-24 w-24 items-center justify-center rounded-full bg-wheat-500/90 text-forest-900 font-serif text-lg rotate-[-8deg]">since<br/>2026</div>
        </div>
      </section>

      {/* Trust band */}
      <section className="border-y border-forest-900/5 bg-surface/60 py-12">
        <div className="container-refined grid md:grid-cols-3 gap-10 items-start">
          <div>
            <p className="eyebrow">{t("home.trust_title")}</p>
            <h2 className="mt-3 font-serif text-3xl text-forest-900 leading-tight">A calling, not a service.</h2>
          </div>
          <p className="md:col-span-2 text-forest-700 text-base leading-relaxed">{t("home.trust_body")}</p>
        </div>
      </section>

      {/* Pricing transparency */}
      <section className="container-refined py-20 md:py-24" data-testid="pricing-section">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">{t("pricing.eyebrow")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest-900 leading-[1.1]">{t("pricing.title")}</h2>
            <p className="mt-6 text-forest-700 leading-relaxed">{t("pricing.body")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/fundraiser" className="btn-primary" data-testid="home-cta-fundraiser">
                {t("pricing.see_proof")} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/support" className="btn-secondary" data-testid="home-cta-support">
                {t("nav.support")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 grid gap-4" data-testid="home-services-list">
            {services.map((s) => (
              <div key={s.id} className="card-warm !p-6 flex items-center justify-between gap-6" data-testid={`home-service-${s.id}`}>
                <div>
                  <p className="eyebrow">{s.duration_minutes} min · {s.setup_type?.replace(/_/g, " ")}</p>
                  <h3 className="mt-2 font-serif text-2xl text-forest-900">{s.name}</h3>
                  <p className="mt-2 text-sm text-forest-700 leading-relaxed max-w-xl">{s.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-serif text-4xl text-forest-900">€{Number(s.price).toFixed(0)}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-widest text-wheat-700">→ Ukraine</p>
                </div>
              </div>
            ))}
            {services.length === 0 && (
              <p className="text-sm text-forest-700">{t("common.loading")}</p>
            )}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="container-refined py-20 md:py-28">
        <div className="max-w-3xl">
          <p className="eyebrow">{t("home.phases_title")}</p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest-900">One long path, honestly walked.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: Leaf, title: t("home.phase1_title"), body: t("home.phase1_body"), tag: "Now" },
            { icon: Sparkles, title: t("home.phase2_title"), body: t("home.phase2_body"), tag: "Later" },
            { icon: Compass, title: t("home.phase3_title"), body: t("home.phase3_body"), tag: "Much later" },
          ].map((p, idx) => (
            <div key={idx} className="card-warm relative overflow-hidden" data-testid={`phase-card-${idx}`}>
              <span className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-wheat-700 bg-wheat-50 rounded-full px-3 py-1 border border-wheat-500/30">{p.tag}</span>
              <p.icon className="h-6 w-6 text-forest-800" strokeWidth={1.25} />
              <h3 className="mt-5 font-serif text-2xl text-forest-900">{p.title}</h3>
              <p className="mt-3 text-sm text-forest-700 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ukraine */}
      <section className="relative overflow-hidden">
        <div className="container-refined grid lg:grid-cols-12 gap-10 items-center py-20 md:py-28">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="eyebrow"><HeartHandshake className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5}/> {t("home.ukraine_title")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest-900">Fields of gold, prayers of peace.</h2>
            <p className="mt-6 text-forest-700 leading-relaxed">{t("home.ukraine_body")}</p>
            <div className="mt-8 flex gap-3">
              <Link to="/mission" className="btn-primary" data-testid="home-cta-mission">{t("home.ukraine_cta")}</Link>
              <a href="https://dzygaspaw.com/en" target="_blank" rel="noreferrer" className="btn-secondary" data-testid="home-cta-dzyga">Dzyga&apos;s Paw ↗</a>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-[2rem]">
              <img src={WHEAT} alt="Golden wheat field" className="w-full h-[380px] md:h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-forest-900/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <p className="font-serif text-ivory text-2xl italic leading-snug max-w-md">&ldquo;{t("home.ukraine_title")}.&rdquo;</p>
                <span className="text-[10px] uppercase tracking-[0.25em] text-ivory/80">Ukraine</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certificates teaser */}
      <section className="container-refined py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="relative overflow-hidden rounded-[2rem]">
              <img src={HEALING} alt="Healing hands" className="w-full h-[360px] md:h-[440px] object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="eyebrow">{t("home.certificates_teaser")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest-900">Committed to real learning.</h2>
            <p className="mt-6 text-forest-700 leading-relaxed max-w-xl">{t("home.certificates_teaser_body")}</p>
            <div className="mt-8">
              <Link to="/certificates" className="btn-ghost-link" data-testid="home-cta-certs">
                {t("home.certificates_cta")} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
